import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ditechDocumentsApi } from '../constants/urls';

const DiTechDocumentViewPage = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDoc = async () => {
      try {
        const res = await axios.get(`${ditechDocumentsApi}/${docId}`, { withCredentials: true });
        if (res.data.success) setDoc(res.data.document);
        else setError('Document not found.');
      } catch (e) {
        setError('Failed to load document.');
      } finally {
        setLoading(false);
      }
    };
    fetchDoc();
  }, [docId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading…</p>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Document not found.'}</p>
          <button onClick={() => navigate(-1)} className="text-blue-600 hover:underline text-sm">Go back</button>
        </div>
      </div>
    );
  }

  const refs = doc.processedReferences || [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:text-blue-800 mb-3 flex items-center gap-1">
            ← Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{doc.documentName}</h1>
          <div className="flex gap-6 mt-2 text-sm text-gray-500">
            {doc.originalFilename && <span>File: {doc.originalFilename}</span>}
            <span>{refs.length} references</span>
            {doc.uploadedBy && <span>Processed by: {doc.uploadedBy}</span>}
            {doc.createdAt && <span>{new Date(doc.createdAt).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Reference list */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">References (Vancouver / DiTech style)</h2>
          </div>

          {refs.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No references saved for this document.</div>
          ) : (
            <ol className="divide-y divide-gray-100">
              {refs.map((ref, idx) => {
                const text = ref.finalStr || ref.term || (typeof ref === 'string' ? ref : '');
                return (
                  <li key={idx} className="px-6 py-4 flex gap-4">
                    <span className="text-gray-400 font-mono text-sm min-w-[2rem] pt-0.5">{idx + 1}.</span>
                    <span className="text-gray-800 text-sm leading-relaxed">{text}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiTechDocumentViewPage;
